import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "utils/context";
import {
  ADDITIONAL_AMOUNT_STATUS,
  CONTRACT_STATUS,
  PMA_STAGES,
  backSteps,
  additionalAmountStates,
  createPrxStates,
  initialsContractData,
  initialsRiskData,
} from "./services/data.transformers";
import { digitsArToFa, digitsFaToEn } from "@persian-tools/persian-tools";
import { useSelector } from "react-redux";

import Header from "./components/stages/header";
import OnbStages from "./components/stages/stages";

import { startPmaOnb, submitPmaOnb } from "./services/service";
import PrepaymentAmount from "./components/pages/prepayment/amount/amount";
import PrepaymentMethod from "./components/pages/prepayment/payment/payment";
import PmaRiskAssessment from "./components/pages/riskAssessment/riskAssessment";
import PmaContract from "./components/pages/contract/contract";
import PmaAdditionalAmount from "./components/pages/additionalAmount/additionalAmount";

import PmaLoading from "./components/loading/pmaLoading";

import PageLayout from "./components/layout/layout";
import "./index.scss";

const PMAOnboarding = () => {
  const { instance } = useAppContext();

  const navigate = useNavigate();
  const pmaOnboarding = useSelector((state) => state?.pmaReducer?.onboarding);

  const [stepStage, setStepStage] = useState(0);
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState(null);
  const [currentStageKey, setCurrentStageKey] = useState(null);

  const [contractStatus, setContractStatus] = useState(CONTRACT_STATUS.LIST);
  const [additionalStatus, setAdditionalStatus] = useState(
    ADDITIONAL_AMOUNT_STATUS.FORM
  );
  const [addressData, setAddressData] = useState(null);

  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [boardLoading, setBoardLoading] = useState(false);
  const [otpError, setOtpError] = useState("");

  const handleChangeContractStates = (currentStatus) => {
    setContractStatus(currentStatus);
  };

  const handleUpdateAddressData = (currentData) => {
    setAddressData(currentData);
  };

  const handleCurrentStage = (res) =>
    res?.stages?.find((item) => item.key === res?.current_stage) ?? {};

  const currentStageDetails = handleCurrentStage(data);
  const onbUuid = pmaOnboarding?.onb_uuid;
  const planId =
    pmaOnboarding?.id || pmaOnboarding?.ds_code || pmaOnboarding?.plan_id;

  const isFindAdditionalAmount = data?.stages?.some(
    (item) => `${item.key}`.toLowerCase() === PMA_STAGES.ADDITIONAL_AMOUNT
  );
  const findPrxStage = data?.stages?.find(
    (item) => `${item.key}`.toLowerCase() === PMA_STAGES.CREATING_PRX
  );

  const handleCheckAdditionalAmount = (pmaData) => {
    const findAdditionalAmount = pmaData?.stages?.find(
      (item) => `${item.key}`.toLowerCase() === PMA_STAGES.ADDITIONAL_AMOUNT
    );

    return additionalAmountStates(findAdditionalAmount ?? {});
  };

  const handlePostStartOnb = async (params, { isShowStages } = {}) => {
    const { data: pmaData } = await startPmaOnb(instance, params);
    if (pmaData) {
      const { isStage: additionalAmountStage, isDone: additionalAmountIsDone } =
        handleCheckAdditionalAmount(pmaData);

      setAmount(params?.amount);
      setStepStage(handleCurrentStage(pmaData)?.sort);
      setData(pmaData);

      if (isShowStages || (additionalAmountStage && additionalAmountIsDone))
        return setCurrentStageKey(PMA_STAGES.STAGES);

      setCurrentStageKey(`${pmaData?.current_stage}`.toLowerCase());
    }
  };

  const startOnbChecking = useCallback(
    async ({ isShowStages } = {}) => {
      if (!onbUuid) {
        setLoading(false);
        setCurrentStageKey(PMA_STAGES.AMOUNT);
        return;
      }

      const params = {
        amount: pmaOnboarding?.amount,
        ds_code: pmaOnboarding?.plan_id,
        onb_uuid: onbUuid,
        type: "PMA",
      };
      setLoading(true);
      await handlePostStartOnb(params, { isShowStages });
      setLoading(false);
    },
    [instance]
  );

  const handleRenderCurrentStage = async () => {
    const { isStage: createPrxStage } = createPrxStates(currentStageDetails);
    const { isStage: additionalAmountStage, isDone: additionalAmountIsDone } =
      additionalAmountStates(currentStageDetails);

    if (
      createPrxStage &&
      !createPrxStates(findPrxStage).isDone &&
      isFindAdditionalAmount
    )
      return handleFinishCreatePrx();

    if (createPrxStage || (additionalAmountStage && additionalAmountIsDone))
      return navigate(`/pma/${planId}`, { replace: true });

    setCurrentStageKey(`${data.current_stage}`.toLowerCase());
  };

  const goToCurrentStage = (stage) => {
    const newData = {
      ...data,
      current_stage: stage,
    };

    setStepStage(handleCurrentStage(newData)?.sort);
    setData(newData);
    setCurrentStageKey(PMA_STAGES[stage]);
  };

  const handleStageShow = () => setCurrentStageKey(PMA_STAGES.STAGES);

  const handleCloseContract = () => setCurrentStageKey(PMA_STAGES.STAGES);

  const goBack = () => {
    if (
      currentStageKey === PMA_STAGES.SIGN_CONTRACT &&
      [CONTRACT_STATUS.VIEW, CONTRACT_STATUS.OTP].includes(contractStatus)
    ) {
      handleChangeContractStates(CONTRACT_STATUS.LIST);
      setOtpError("");
      return;
    }
    backSteps({
      planId,
      currentStageKey,
      navigate,
      setCurrentStageKey,
      startOnbChecking,
    });
  };

  const handleSubmitOnb = async (formData, { callback } = {}) => {
    setBoardLoading(true);
    const { data: pmaData } = await submitPmaOnb(instance, formData);

    if (pmaData) {
      setStepStage(handleCurrentStage(pmaData)?.sort);
      setData(pmaData);
      if (callback) callback();
    }

    setBoardLoading(false);
  };

  const handleFinishAmount = async (value) => {
    const params = {
      type: "PMA",
      ds_code: pmaOnboarding.id,
      amount: `${digitsFaToEn(
        digitsArToFa(`${value?.amount}`.replaceAll(",", ""))
      )}`,
    };

    setPaymentLoading(true);
    await handlePostStartOnb(params, { isShowStages: true });
    setPaymentLoading(false);
  };

  const handleFinishRisk = async (lastSurveyData) => {
    const newCurrentStage = { ...currentStageDetails };
    if (newCurrentStage?.fields?.[0]?.key)
      newCurrentStage.fields[0].value = lastSurveyData?.user_survey_id;

    const newData = initialsRiskData({
      data,
      currentStageDetails,
      lastSurveyData,
    });

    setBoardLoading(true);

    const { data: pmaData } = await submitPmaOnb(instance, newData);

    if (pmaData) {
      setStepStage(handleCurrentStage(pmaData)?.sort);
      setCurrentStageKey(`${pmaData?.current_stage}`.toLowerCase());
      setData(pmaData);
    }
    setBoardLoading(false);
  };

  const handleFinishContract = async ({
    addressData,
    setOtpSuccess,
    value,
    setSendingOTP,
  }) => {
    const newData = initialsContractData({
      addressData,
      currentStageDetails,
      data,
      value,
    });

    setSendingOTP(true);
    const { data: pmaData } = await submitPmaOnb(instance, newData);

    const findSignContractStage = pmaData?.stages?.find(
      (item) => `${item.key}`.toLowerCase() === PMA_STAGES.SIGN_CONTRACT
    );

    if (pmaData && findSignContractStage?.status === "FAILED") {
      setOtpError(findSignContractStage?.message?.[0]);
      setSendingOTP(false);
      return;
    }

    if (pmaData) {
      setStepStage(handleCurrentStage(pmaData)?.sort);
      setData(pmaData);
      if (setOtpSuccess) setOtpSuccess(true);
    }

    setSendingOTP(false);
  };

  const handleFinishCreatePrx = async () => {
    handleSubmitOnb(data);
  };

  const handleFinishAdditionalAmount = () => {
    handleSubmitOnb(data, {
      callback: setAdditionalStatus(ADDITIONAL_AMOUNT_STATUS.RECEIPT),
    });
  };

  const handleShowBackArrow = () => {
    const isArrowOnContact =
      [PMA_STAGES.SIGN_CONTRACT].includes(currentStageKey) &&
      [CONTRACT_STATUS.OTP, CONTRACT_STATUS.VIEW].includes(contractStatus);

    const isArrowOnAmount =
      [PMA_STAGES.ADDITIONAL_AMOUNT].includes(currentStageKey) &&
      [ADDITIONAL_AMOUNT_STATUS.RECEIPT].includes(additionalStatus);

    return !loading && !isArrowOnContact && !isArrowOnAmount;
  };

  useEffect(() => {
    if ([undefined, null].includes(pmaOnboarding)) navigate("/pma");
    startOnbChecking();
  }, []);

  const renderPMAStages = () => {
    switch (currentStageKey) {
      case PMA_STAGES.AMOUNT:
        return (
          <PrepaymentAmount
            amount={amount}
            loading={paymentLoading}
            pmaOnboarding={pmaOnboarding}
            onFinishAmount={handleFinishAmount}
          />
        );

      case PMA_STAGES.CREATING_PRX:
      case PMA_STAGES.STAGES:
        return (
          <>
            <Header />
            <OnbStages
              currentStageDetails={currentStageDetails}
              data={data}
              isFindAdditionalAmount={isFindAdditionalAmount}
              loading={boardLoading}
              stepStage={stepStage}
              pmaOnboarding={pmaOnboarding}
              planId={planId}
              onClick={handleRenderCurrentStage}
              onChangeContractStates={handleChangeContractStates}
              goToCurrentStage={goToCurrentStage}
            />
          </>
        );

      case PMA_STAGES.PAYMENT:
        return (
          <PrepaymentMethod
            amount={amount}
            currentStageDetails={currentStageDetails}
          />
        );

      case PMA_STAGES.RISK:
        return (
          <PmaRiskAssessment
            boardLoading={boardLoading}
            currentStageDetails={currentStageDetails}
            onFinishRisk={handleFinishRisk}
          />
        );

      case PMA_STAGES.SIGN_CONTRACT:
        return (
          <PmaContract
            addressData={addressData}
            boardLoading={boardLoading}
            currentStageDetails={currentStageDetails}
            contractStatus={contractStatus}
            data={data}
            otpError={otpError}
            onbUuid={data?.onb_uuid}
            onCloseContract={handleCloseContract}
            onChangeContractStates={handleChangeContractStates}
            onFinishContract={handleFinishContract}
            onUpdateAddressData={handleUpdateAddressData}
          />
        );

      case PMA_STAGES.ADDITIONAL_AMOUNT:
        return (
          <PmaAdditionalAmount
            additionalStatus={additionalStatus}
            currentStageDetails={currentStageDetails}
            boardLoading={boardLoading}
            onbUuid={onbUuid}
            onFinishAdditionalAmount={handleFinishAdditionalAmount}
            onStageShow={handleStageShow}
          />
        );

      default:
        return "";
    }
  };

  return (
    <PageLayout
      currentStageKey={currentStageKey}
      data={data}
      goBack={goBack}
      loading={loading}
      showBackArrow={handleShowBackArrow()}
      stepStage={stepStage}
      pmaOnboarding={pmaOnboarding}
    >
      <>{loading ? <PmaLoading /> : renderPMAStages()}</>
    </PageLayout>
  );
};
export default PMAOnboarding;
